import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { SelectPopup } from './select-popup'
import type { SelectOption } from './select-popup'
import styles from './select.module.css'

export type { SelectOption } from './select-popup'

type Props<V extends string> = {
  value: V | null
  onChange: (value: V) => void
  options: readonly SelectOption<V>[]
  placeholder?: string
  columns?: number
  sheetColumns?: number
  round?: boolean
  title?: string
  hint?: string
  sheet?: 'auto' | 'always' | 'never'
  size?: 'md' | 'sm'
  disabled?: boolean
  invalid?: boolean
  id?: string
  className?: string
  'aria-label'?: string
  onOpenChange?: (open: boolean) => void
}

const SHEET_QUERY = '(max-width: 560px)'
const TYPEAHEAD_MS = 700

const Chevron = () => (
  <svg className={styles.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9.5l6 6 6-6" />
  </svg>
)

export function Select<V extends string>({
  value,
  onChange,
  options,
  placeholder = '',
  columns = 1,
  sheetColumns,
  round = false,
  title,
  hint,
  sheet = 'auto',
  size = 'md',
  disabled = false,
  invalid = false,
  id,
  className,
  'aria-label': ariaLabel,
  onOpenChange,
}: Props<V>) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [asSheet, setAsSheet] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const typed = useRef({ text: '', at: 0 })
  const listId = useId()

  const selected = options.find((o) => o.value === value)
  const cols = asSheet ? (sheetColumns ?? Math.max(columns, 3)) : columns

  const toggle = useCallback(
    (next: boolean) => {
      if (next) {
        setAsSheet(sheet === 'always' || (sheet === 'auto' && window.matchMedia(SHEET_QUERY).matches))
        setActive(Math.max(0, options.findIndex((o) => o.value === value)))
      }
      setOpen(next)
      onOpenChange?.(next)
    },
    [sheet, options, value, onOpenChange],
  )

  const pick = useCallback(
    (index: number) => {
      const option = options[index]
      if (!option || option.disabled) return
      onChange(option.value)
      toggle(false)
      triggerRef.current?.focus({ preventScroll: true })
    },
    [options, onChange, toggle],
  )

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const target = e.target
      if (!(target instanceof Node)) return
      if (rootRef.current?.contains(target) || popupRef.current?.contains(target)) return
      toggle(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open, toggle])

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    const last = options.length - 1
    const move = (delta: number) => {
      e.preventDefault()
      if (!open) return toggle(true)
      setActive((i) => Math.min(last, Math.max(0, i + delta)))
    }
    switch (e.key) {
      case 'ArrowDown':
        return move(cols)
      case 'ArrowUp':
        return move(-cols)
      case 'ArrowRight':
        return open && cols > 1 ? move(1) : undefined
      case 'ArrowLeft':
        return open && cols > 1 ? move(-1) : undefined
      case 'Home':
        return open ? move(-options.length) : undefined
      case 'End':
        return open ? move(options.length) : undefined
      case 'Enter':
      case ' ':
        e.preventDefault()
        return open ? pick(active) : toggle(true)
      case 'Escape':
        if (!open) return undefined
        e.preventDefault()
        e.stopPropagation()
        return toggle(false)
      case 'Tab':
        return open ? toggle(false) : undefined
    }
    if (e.key.length !== 1 || !/\S/.test(e.key)) return
    const now = Date.now()
    const text = (now - typed.current.at < TYPEAHEAD_MS ? typed.current.text : '') + e.key.toLowerCase()
    typed.current = { text, at: now }
    const found = options.findIndex((o) => o.label.toLowerCase().startsWith(text))
    if (found < 0) return
    if (!open) toggle(true)
    setActive(found)
  }

  const rootClass = [styles.root, size === 'sm' ? styles.sm : '', open ? styles.open : '', invalid ? styles.invalid : '', disabled ? styles.disabled : '', className ?? '']
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={rootRef} className={rootClass}>
      <div
        ref={triggerRef}
        id={id}
        className={styles.trigger}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-disabled={disabled || undefined}
        aria-invalid={invalid || undefined}
        onClick={() => !disabled && toggle(!open)}
        onKeyDown={onKeyDown}
      >
        <span className={selected ? styles.value : `${styles.value} ${styles.placeholder}`}>{selected?.label ?? placeholder}</span>
        <Chevron />
      </div>
      {open && (
        <SelectPopup
          ref={popupRef}
          anchor={rootRef}
          small={size === 'sm'}
          id={listId}
          options={options}
          value={value}
          active={active}
          columns={cols}
          round={round}
          sheet={asSheet}
          title={title ?? placeholder}
          hint={hint}
          onActive={setActive}
          onPick={pick}
          onClose={() => toggle(false)}
        />
      )}
    </div>
  )
}
